// CalenderMarker dynamische Funktionen
import React from 'react';
import apiService from '../services/apiService';

// Cache für geladene Marker um wiederholte API-Calls zu vermeiden
let cachedMarkers = null;
let cacheExpiry = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten

// Funktion zum Laden der Kalender Marker aus der API
export const loadCalenderMarkerOptions = async () => {
  // Prüfe Cache
  if (cachedMarkers && cacheExpiry && Date.now() < cacheExpiry) {
    return cachedMarkers;
  }

  try {
    const response = await apiService.getCalendarMarkers();
    const markers = response.markers || [];
    
    // Erstelle CALENDER_MARKER_OPTIONS-kompatibles Format
    const options = markers.map(marker => ({
      value: marker.value,
      label: marker.label,
      icon: marker.icon,
      color: marker.color,
      sortOrder: marker.sortOrder
    }));

    // Cache aktualisieren
    cachedMarkers = options;
    cacheExpiry = Date.now() + CACHE_DURATION;
    
    return options;
  } catch (error) {
    console.error('Error loading calendar markers:', error);
    
    // Fallback zu hardcoded Markern wenn API fehlschlägt
    const fallbackMarkers = [
      { value: 'extern', label: 'Extern', icon: '🚗', color: 'var(--color-primary)', sortOrder: 1 },
      { value: 'intern', label: 'Intern', icon: '🏠', color: 'var(--color-success-alt)', sortOrder: 2 },
      { value: 'zeitlich_fix', label: 'Zeitlich Fix', icon: '⏰', color: 'var(--color-orange)', sortOrder: 3 },
      { value: 'dnd', label: 'DND', icon: '🚫', color: 'var(--color-danger)', sortOrder: 4 }
    ];
    
    cachedMarkers = fallbackMarkers;
    cacheExpiry = Date.now() + CACHE_DURATION;
    
    return fallbackMarkers;
  }
};

// Legacy-Export für Rückwärtskompatibilität (wird nach Migration entfernt)
export let CALENDER_MARKER_OPTIONS = [];

// Initialisiere die Options beim ersten Import und warte darauf
loadCalenderMarkerOptions().then(options => {
  CALENDER_MARKER_OPTIONS = options;
}).catch(error => {
  console.error('Failed to initialize calendar markers:', error);
  // Fallback zu hardcoded Markern
  CALENDER_MARKER_OPTIONS = [
    { value: 'extern', label: 'Extern', icon: '🚗', color: 'var(--color-primary)', sortOrder: 1 },
    { value: 'intern', label: 'Intern', icon: '🏠', color: 'var(--color-success-alt)', sortOrder: 2 },
    { value: 'zeitlich_fix', label: 'Zeitlich Fix', icon: '⏰', color: 'var(--color-orange)', sortOrder: 3 },
    { value: 'dnd', label: 'DND', icon: '🚫', color: 'var(--color-danger)', sortOrder: 4 }
  ];
});

// Funktion zum Cache-Invalidierung (wird nach Admin-Änderungen aufgerufen)
export const invalidateMarkerCache = () => {
  cachedMarkers = null;
  cacheExpiry = null;
};

// Helper-Funktion um die Konfiguration für einen Marker-Typ zu bekommen (asynchron)
export const getCalenderMarkerConfig = async (markerType) => {
  const options = await loadCalenderMarkerOptions();
  return options.find(option => option.value === markerType);
};

// Helper-Funktion um das Symbol als Text zu bekommen (für Listen und Kalender) (asynchron)
export const getCalenderMarkerIcon = async (markerType) => {
  const config = await getCalenderMarkerConfig(markerType);
  return config ? config.icon : '';
};

// Helper-Funktion um das Label zu bekommen (asynchron)
export const getCalenderMarkerLabel = async (markerType) => {
  const config = await getCalenderMarkerConfig(markerType);
  return config ? config.label : '';
};

// Synchrone Helper-Funktionen für Fallback (verwenden Cache oder hardcoded Fallback)
export const getCalenderMarkerConfigSync = (markerType) => {
  // Versuche zuerst den Cache
  if (cachedMarkers && cachedMarkers.length > 0) {
    return cachedMarkers.find(option => option.value === markerType);
  }
  
  // Fallback zu Legacy-Array
  if (CALENDER_MARKER_OPTIONS && CALENDER_MARKER_OPTIONS.length > 0) {
    return CALENDER_MARKER_OPTIONS.find(option => option.value === markerType);
  }
  
  // Hardcoded Fallback als letzte Option
  const hardcodedFallback = [
    { value: 'extern', label: 'Extern', icon: '🚗', color: 'var(--color-primary)', sortOrder: 1 },
    { value: 'intern', label: 'Intern', icon: '🏠', color: 'var(--color-success-alt)', sortOrder: 2 },
    { value: 'zeitlich_fix', label: 'Zeitlich Fix', icon: '⏰', color: 'var(--color-orange)', sortOrder: 3 },
    { value: 'dnd', label: 'DND', icon: '🚫', color: 'var(--color-danger)', sortOrder: 4 }
  ];
  
  return hardcodedFallback.find(option => option.value === markerType) || null;
};

export const getCalenderMarkerIconSync = (markerType) => {
  const config = getCalenderMarkerConfigSync(markerType);
  return config ? config.icon : '';
};

export const getCalenderMarkerLabelSync = (markerType) => {
  const config = getCalenderMarkerConfigSync(markerType);
  return config ? config.label : '';
};

// Helper-Funktion um die Farbe zu bekommen
export const getCalenderMarkerColor = (markerType) => {
  const config = getCalenderMarkerConfig(markerType);
  return config ? config.color : 'var(--color-text-muted)';
};

// Parser für MySQL SET-Werte (komma-separiert) zu Array
export const parseCalenderMarkers = (markersString) => {
  if (!markersString || markersString.trim() === '') {
    return [];
  }
  return markersString.split(',').map(marker => marker.trim()).filter(marker => marker);
};

// Array zu MySQL SET-String (komma-separiert)
export const stringifyCalenderMarkers = (markersArray) => {
  if (!markersArray || markersArray.length === 0) {
    return null;
  }
  return markersArray.join(',');
};

// React-Komponente für die Anzeige des CalenderMarker-Icons (einzeln) (mit dynamischen Markern)
export const CalenderMarkerDisplay = ({ markerType, size = 16, showText = false }) => {
  const [config, setConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    const loadConfig = async () => {
      try {
        const markerConfig = await getCalenderMarkerConfig(markerType);
        setConfig(markerConfig);
      } catch (error) {
        console.error('Error loading marker config:', error);
        // Fallback zu synchroner Funktion
        const fallbackConfig = getCalenderMarkerConfigSync(markerType);
        setConfig(fallbackConfig);
      } finally {
        setLoading(false);
      }
    };
    
    loadConfig();
  }, [markerType]);
  
  if (loading) {
    return (
      <span 
        style={{ 
          display: 'inline-flex', 
          alignItems: 'center',
          fontSize: `${size}px`,
          opacity: 0.5
        }}
      >
        ⏳
      </span>
    );
  }
  
  if (!config) {
    return null;
  }

  return (
    <span 
      style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: showText ? '4px' : '0',
        color: config.color,
        fontSize: `${size}px`
      }}
      title={config.label}
    >
      {config.icon}
      {showText && <span style={{ fontSize: `${size - 2}px` }}>{config.label}</span>}
    </span>
  );
};

// React-Komponente für die Anzeige mehrerer CalenderMarker-Icons
export const CalenderMarkersDisplay = ({ markers, size = 16, showText = false }) => {
  const markersArray = typeof markers === 'string' ? parseCalenderMarkers(markers) : (markers || []);
  
  if (!markersArray || markersArray.length === 0) {
    return null;
  }

  return (
    <span 
      style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: '2px'
      }}
    >
      {markersArray.map((markerType, index) => (
        <CalenderMarkerDisplay 
          key={index}
          markerType={markerType} 
          size={size} 
          showText={showText}
        />
      ))}
    </span>
  );
};

// React-Komponente für Mehrfach-Auswahl mit Checkboxes (mit dynamischen Markern)
export const CalenderMarkerSelect = ({ value, onChange, placeholder = "Kalender-Marker auswählen..." }) => {
  const [markerOptions, setMarkerOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  
  // Lade Marker-Optionen beim Mount
  React.useEffect(() => {
    const loadOptions = async () => {
      try {
        const options = await loadCalenderMarkerOptions();
        setMarkerOptions(options);
      } catch (error) {
        console.error('Error loading marker options:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadOptions();
  }, []);
  
  // Parse current value to array
  const selectedMarkers = typeof value === 'string' ? parseCalenderMarkers(value) : (value || []);
  
  const handleCheckboxChange = (markerValue, isChecked) => {
    let newSelectedMarkers;
    if (isChecked) {
      // Add marker if not already present
      newSelectedMarkers = selectedMarkers.includes(markerValue) 
        ? selectedMarkers 
        : [...selectedMarkers, markerValue];
    } else {
      // Remove marker
      newSelectedMarkers = selectedMarkers.filter(marker => marker !== markerValue);
    }
    
    // Convert back to string format for MySQL SET
    const stringValue = stringifyCalenderMarkers(newSelectedMarkers);
    onChange(stringValue);
  };

  if (loading) {
    return (
      <div
        style={{
          border: '1px solid var(--color-border-neutral)',
          borderRadius: '4px',
          padding: '8px',
          fontSize: '14px',
          backgroundColor: 'white',
          color: 'var(--color-text-light)'
        }}
      >
        Lade Marker-Optionen...
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid var(--color-border-neutral)',
        borderRadius: '4px',
        padding: '8px',
        fontSize: '14px',
        backgroundColor: 'white'
      }}
    >
      <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginBottom: '8px' }}>
        {placeholder}
      </div>
      {markerOptions.map(option => (
        <label 
          key={option.value} 
          style={{ 
            display: 'block', 
            marginBottom: '6px', 
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <input
            type="checkbox"
            checked={selectedMarkers.includes(option.value)}
            onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          <span style={{ fontSize: '16px', marginRight: '6px' }}>{option.icon}</span>
          <span>{option.label}</span>
        </label>
      ))}
      {selectedMarkers.length === 0 && (
        <div style={{ fontSize: '12px', color: 'var(--color-text-placeholder)', fontStyle: 'italic' }}>
          Keine Marker ausgewählt
        </div>
      )}
    </div>
  );
};

// Kompakte React-Komponente für Modal-Header (nur Symbole + Checkboxes) (mit dynamischen Markern)
export const CalenderMarkerHeaderSelect = ({ value, onChange }) => {
  const [markerOptions, setMarkerOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  
  // Lade Marker-Optionen beim Mount
  React.useEffect(() => {
    const loadOptions = async () => {
      try {
        const options = await loadCalenderMarkerOptions();
        setMarkerOptions(options);
      } catch (error) {
        console.error('Error loading marker options:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadOptions();
  }, []);
  
  // Parse current value to array
  const selectedMarkers = typeof value === 'string' ? parseCalenderMarkers(value) : (value || []);
  
  const handleCheckboxChange = (markerValue, isChecked) => {
    let newSelectedMarkers;
    if (isChecked) {
      newSelectedMarkers = selectedMarkers.includes(markerValue) 
        ? selectedMarkers 
        : [...selectedMarkers, markerValue];
    } else {
      newSelectedMarkers = selectedMarkers.filter(marker => marker !== markerValue);
    }
    
    const stringValue = stringifyCalenderMarkers(newSelectedMarkers);
    onChange(stringValue);
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: '16px',
          fontSize: '12px',
          color: 'var(--color-text-light)'
        }}
      >
        Lade...
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginLeft: '16px'
      }}
    >
      {markerOptions.map(option => (
        <label 
          key={option.value} 
          style={{ 
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            gap: '4px'
          }}
          title={option.label}
        >
          <input
            type="checkbox"
            checked={selectedMarkers.includes(option.value)}
            onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
            style={{ 
              margin: '0',
              width: '16px',
              height: '16px'
            }}
          />
          <span style={{ fontSize: '18px' }}>{option.icon}</span>
        </label>
      ))}
    </div>
  );
};

// React-Komponente für Kalender-Marker-Anzeige (ersetzt ::before) (mit dynamischen Markern)
// #604: `inline` = ohne eigene Position, die Marker nebeneinander. So steht die
// Anzeige in der Kopfzeile des Kalenderblocks (components/kalender/KalenderTicketKennung).
export const CalenderMarkersCalendarDisplay = ({ markers, position = { top: '2px', right: '2px' }, inline = false }) => {
  const [markerConfigs, setMarkerConfigs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  
  const markersArray = typeof markers === 'string' ? parseCalenderMarkers(markers) : (markers || []);
  
  React.useEffect(() => {
    const loadConfigs = async () => {
      if (!markersArray || markersArray.length === 0) {
        setMarkerConfigs([]);
        setLoading(false);
        return;
      }

      try {
        const configs = await Promise.all(
          markersArray.map(async (markerType) => {
            const config = await getCalenderMarkerConfig(markerType);
            return config ? { ...config, markerType } : null;
          })
        );
        
        setMarkerConfigs(configs.filter(config => config !== null));
      } catch (error) {
        console.error('Error loading marker configs:', error);
        // Fallback zu synchronen Funktionen
        const fallbackConfigs = markersArray.map(markerType => {
          const config = getCalenderMarkerConfigSync(markerType);
          return config ? { ...config, markerType } : null;
        }).filter(config => config !== null);
        
        setMarkerConfigs(fallbackConfigs);
      } finally {
        setLoading(false);
      }
    };
    
    loadConfigs();
  }, [markers]);
  
  const lage = inline
    ? { display: 'flex', flexDirection: 'row', gap: '1px', pointerEvents: 'none' }
    : { position: 'absolute', top: position.top, right: position.right, display: 'flex', flexDirection: 'column', gap: '1px', zIndex: 30, pointerEvents: 'none' };

  if (loading) {
    return (
      <div
        style={{
          ...lage,
          backgroundColor: 'var(--color-glass-90)',
          borderRadius: '3px',
          boxShadow: '0 1px 3px var(--color-shadow-30)',
          opacity: 0.5
        }}
      >
        <span style={{ fontSize: 'var(--text-micro)', padding: '2px' }}>⏳</span>
      </div>
    );
  }
  
  if (!markerConfigs || markerConfigs.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        ...lage,
        backgroundColor: 'var(--color-glass-90)',
        borderRadius: '3px',
        boxShadow: '0 1px 3px var(--color-shadow-30)'
      }}
    >
      {markerConfigs.map((config, index) => (
        <span
          key={index}
          style={{
            fontSize: '12px',
            lineHeight: '12px',
            display: 'block',
            textAlign: 'center'
          }}
          title={config.label}
        >
          {config.icon}
        </span>
      ))}
    </div>
  );
}; 